import React from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import {
    Dialog,
    DialogContent,
    DialogFooter,
} from 'components/ui/dialog';
import { Button } from 'components/ui/button';
import { Label } from 'components/ui/label';
import { Input } from 'components/ui/input';
import { Form, FormField, FormItem, FormControl, FormMessage } from 'components/ui/form';
import { useTheme } from 'components/core/theme-provider';
import { useWhiteLabelStore } from 'state/store/white-label/useWhiteLabelStore';
import { ACCOUNT_QUERY_KEY, useGetAccount, useUpdateAccount } from 'features/profile/hooks/use-account';
import { useCommonStore } from 'state/store/common/useCommonStore';
import groupLight from 'assets/images/bebrave-group.svg';
import groupDark from 'assets/images/bebrave-group-dark.svg';
import bgAuthLight from 'assets/images/bg_auth_light.jpg';
import bgAuthDark from 'assets/images/bg_auth_dark.jpg';

interface FormData {
    firstName: string;
    lastName: string;
}

interface NameRequiredModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onDismiss: () => void;
}

/**
 * NameRequiredModal Component
 *
 * A modal that prompts users to complete their profile by adding their first and last name.
 * This modal is displayed when users don't have their names set up in their profile.
 * Styled using Tailwind and ShadCN dialog components.
 *
 * Features:
 * - Uses `react-hook-form` for form state and validation
 * - Provides input fields for first name and last name
 * - Beautiful welcome design with icon
 *
 * @param open - Controls whether the modal is visible
 * @param onOpenChange - Callback to change the open state
 * @param onDismiss - Callback when the modal is dismissed
 */
const NameRequiredModal: React.FC<NameRequiredModalProps> = ({
    open,
    onOpenChange,
    onDismiss,
}) => {
    const { t } = useTranslation();
    const { theme } = useTheme();
    const queryClient = useQueryClient();
    const { settings: whiteLabelSettings } = useWhiteLabelStore();
    const user = useCommonStore((state) => state.user);
    const setUser = useCommonStore((state) => state.setUser);
    const { refetch: refetchAccount } = useGetAccount({ enabled: false });

    const { mutate: updateAccount, isPending } = useUpdateAccount({
        onSuccess: async () => {
            queryClient.invalidateQueries({ queryKey: ACCOUNT_QUERY_KEY });

            // Refetch account data and update common store
            const { data: updatedAccount } = await refetchAccount();
            if (updatedAccount) {
                setUser(updatedAccount);
            }

            onDismiss();
            onOpenChange(false);
        },
    });

    // Determine which logo to use based on theme and white-label settings
    const logoSrc = theme === 'dark'
        ? (whiteLabelSettings?.logoUrlDarkMode || groupDark)
        : (whiteLabelSettings?.logoUrl || groupLight);

    // Determine which banner to use based on theme and white-label settings
    const loginBannerSrc = theme === 'dark'
        ? (whiteLabelSettings?.loginBannerUrlDarkMode || bgAuthDark)
        : (whiteLabelSettings?.loginBannerUrl || bgAuthLight);

    const getBackgroundImage = () => {
        if (theme === 'system') {
            return window.matchMedia('(prefers-color-scheme: dark)').matches ? bgAuthDark : bgAuthLight;
        }
        return loginBannerSrc;
    };

    const form = useForm<FormData>({
        defaultValues: {
            firstName: '',
            lastName: '',
        },
    });

    const { control, handleSubmit } = form;

    const handleDismiss = () => {
        onDismiss();
        onOpenChange(false);
    };

    const onSubmit = async (data: FormData) => {
        if (!user?.id) {
            return;
        }

        const payload = {
            itemId: user.id,
            firstName: data.firstName.trim(),
            lastName: data.lastName.trim(),
            email: user.email || '',
            country: user.country || '',
        };

        updateAccount(payload);
    };

    const handleDialogClick = (e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="rounded-xl sm:max-w-[700px] p-0 overflow-hidden"
                onClick={handleDialogClick}
            >
                <div className="flex w-full">
                    {/* Left side - Banner image */}
                    <div className="hidden sm:block w-[40%] relative bg-primary-50">
                        <img
                            src={getBackgroundImage()}
                            alt="Welcome banner"
                            className="w-full h-full object-cover"
                            key={theme ?? 'default'}
                        />
                    </div>

                    {/* Right side - Form */}
                    <div className="flex flex-col justify-center w-full sm:w-[60%] px-6 py-8">
                        {/* Logo */}
                        <div className="flex justify-center mb-6">
                            <img
                                src={logoSrc}
                                className="h-8 object-contain"
                                alt="EagleGPT logo"
                                onError={(e) => {
                                    const target = e.target as HTMLImageElement;
                                    target.src = groupLight;
                                }}
                            />
                        </div>

                        {/* Welcome text */}
                        <div className="text-center mb-6">
                            <h2 className="text-xl font-semibold text-foreground mb-2">
                                {t('WELCOME_TO_EAGLEGPT') || 'Welcome to EagleGPT'}
                            </h2>
                            <p className="text-muted-foreground text-sm">
                                Please enter your name to get started
                            </p>
                        </div>

                        {/* Form */}
                        <Form {...form}>
                            <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
                                <FormField
                                    control={control}
                                    name="firstName"
                                    rules={{
                                        required: t('FIRST_NAME_REQUIRED') || 'First name is required',
                                        maxLength: {
                                            value: 15,
                                            message: t('MAX_15_CHARACTERS') || 'Maximum 15 characters allowed',
                                        },
                                    }}
                                    render={({ field }) => (
                                        <FormItem>
                                            <Label className="text-sm font-medium">
                                                {t('FIRST_NAME') || 'First Name'}
                                                <span className="text-destructive ml-0.5">*</span>
                                            </Label>
                                            <FormControl>
                                                <Input
                                                    {...field}
                                                    className="h-10"
                                                    placeholder={t('ENTER_YOUR_FIRST_NAME') || 'Enter first name'}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={control}
                                    name="lastName"
                                    rules={{
                                        required: t('LAST_NAME_REQUIRED') || 'Last name is required',
                                        maxLength: {
                                            value: 15,
                                            message: t('MAX_15_CHARACTERS') || 'Maximum 15 characters allowed',
                                        },
                                    }}
                                    render={({ field }) => (
                                        <FormItem>
                                            <Label className="text-sm font-medium">
                                                {t('LAST_NAME') || 'Last Name'}
                                                <span className="text-destructive ml-0.5">*</span>
                                            </Label>
                                            <FormControl>
                                                <Input
                                                    {...field}
                                                    className="h-10"
                                                    placeholder={t('ENTER_YOUR_LAST_NAME') || 'Enter last name'}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <DialogFooter className="flex flex-col gap-2 pt-4">
                                    <Button
                                        variant="ghost"
                                        type="button"
                                        onClick={handleDismiss}
                                        className="w-full text-muted-foreground"
                                    >
                                        {t('LATER') || 'Later'}
                                    </Button>
                                    <Button type="submit" className="w-full" disabled={isPending}>
                                        {isPending ? (t('SAVING') || 'Saving...') : (t('GET_STARTED') || 'Get Started')}
                                    </Button>
                                </DialogFooter>
                            </form>
                        </Form>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default NameRequiredModal;

